import React, { useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { ChevronRight, Camera, Upload, Tag, Check, X } from 'lucide-react';

const toJpeg = (dataUrl, maxDim = 1920) =>
  new Promise((resolve) => {
    const img = document.createElement('img');
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

const TagCaptureScreen = () => {
  const { setCurrentScreen, updateScanData, scanData } = useApp();
  const [images, setImages] = useState(scanData.tag.images || []);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const remainingSlots = 3 - images.length;

    files.slice(0, remainingSlots).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const jpeg = await toJpeg(reader.result);
        setImages(prev => [...prev, jpeg]);
      };
      reader.readAsDataURL(file);
    });

    e.target.value = '';
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('Camera is not supported in this browser. Please use "Upload from Gallery" instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setShowCamera(true);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        alert('Camera permission denied. Please allow camera access in your browser settings, then try again.');
      } else if (err.name === 'NotFoundError') {
        alert('No camera found on this device. Please use "Upload from Gallery" instead.');
      } else {
        alert('Could not access camera. Please use "Upload from Gallery" instead.');
      }
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current && images.length < 3) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setImages(prev => [...prev, imageData]);
      if (images.length >= 2) {
        stopCamera();
      }
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleDone = () => {
    if (images.length > 0) {
      updateScanData({
        tag: { active: true, images: images }
      });
      setCurrentScreen('scan');
    }
  };

  if (showCamera) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col">
        <video ref={videoRef} autoPlay playsInline className="flex-1 w-full object-cover" />
        <canvas ref={canvasRef} className="hidden" />
        <div className="absolute top-4 right-4">
          <button onClick={stopCamera} className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <X size={20} className="text-white" />
          </button>
        </div>
        <div className="absolute bottom-8 left-0 right-0 flex justify-center">
          <button
            onClick={capturePhoto}
            disabled={images.length >= 3}
            className="w-20 h-20 bg-white rounded-full border-4 border-white/50 active:scale-95 transition-transform disabled:opacity-50"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pb-24 flex flex-col">
      <div className="px-6 pt-6 pb-4 flex items-center gap-4">
        <button
          onClick={() => setCurrentScreen('scan')}
          className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center hover:bg-white/20 transition-all flex-shrink-0"
        >
          <ChevronRight size={18} className="text-white rotate-180" />
        </button>
        <img src="/logos/logo-black.jpg" alt="ATELIA" className="h-16 w-auto object-contain" style={{ filter: 'invert(1)' }} />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center mb-8">
          <Tag size={36} className="text-white" strokeWidth={1.5} />
        </div>

        <h1 className="text-3xl font-bold text-white text-center mb-4 uppercase tracking-wide">
          Scan Clothing Tag
        </h1>

        <p className="text-white/70 text-center text-sm max-w-xs mb-8 leading-relaxed">
          Capture clear photos of care labels, composition tags, and brand labels.
        </p>

        {images.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-6 w-full max-w-sm">
            {images.map((img, i) => (
              <div key={i} className="aspect-square bg-white/10 rounded-lg overflow-hidden relative">
                <img src={img} alt={`Tag ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => removeImage(i)}
                  className="absolute -top-1 -right-1 w-6 h-6 bg-black rounded-full flex items-center justify-center hover:bg-black/80"
                >
                  <X size={12} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="w-full max-w-sm space-y-3">
          <button
            onClick={startCamera}
            disabled={images.length >= 3}
            className="w-full bg-white text-black py-4 px-8 rounded-2xl
              font-bold uppercase tracking-wide hover:bg-white/90 transition-all
              flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="tag-camera-btn"
          >
            <Camera size={20} />
            Use Camera
          </button>

          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileUpload} />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={images.length >= 3}
            className="w-full bg-white/10 border border-white/20 text-white py-4 px-8 rounded-2xl
              font-bold uppercase tracking-wide hover:bg-white/20 transition-all
              flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="tag-upload-btn"
          >
            <Upload size={20} />
            Upload from Gallery
          </button>

          {images.length > 0 && (
            <button
              onClick={handleDone}
              className="w-full bg-white text-black py-4 px-8 rounded-2xl
                font-bold uppercase tracking-wide hover:bg-white/90 transition-all
                flex items-center justify-center gap-3"
              data-testid="tag-done-btn"
            >
              <Check size={20} />
              Done ({images.length}/3)
            </button>
          )}
        </div>
      </div>

      <div className="px-6 pb-8">
        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
          <p className="text-white/80 font-semibold text-xs uppercase mb-2">Scanning Tips</p>
          <p className="text-white/60 text-xs leading-relaxed">
            • Ensure good lighting
            • Keep text clearly visible
            • Capture multiple angles if label is large
            • Up to 3 images recommended
          </p>
        </div>
      </div>
    </div>
  );
};

export default TagCaptureScreen;